/**
 * Demo Control Panel — hidden SE drawer.
 *
 * Trigger: Cmd/Ctrl+Shift+D (only visible to CAIO in Demo Mode).
 *
 * Provides quick demo steering:
 *   - Jump to any major page
 *   - Trigger a fake drift alert (UI-only toast)
 *   - Swap to another persona (delegated to RoleSwitcher)
 *   - Reset the demo seed (calls the bundle reload endpoint)
 *   - Toggle "what changed today" banner
 *
 * This component is mounted by Header.jsx but only renders when both
 * `isDemo` and the current user is CAIO. Everyone else sees nothing,
 * even if they happen to press the shortcut.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  X,
  ArrowRight,
  Bell,
  RotateCcw,
  UserCog,
  Activity,
  Keyboard,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useTenantData } from "@/contexts/TenantModeContext";
import { generateAgentDossier } from "@/lib/agentDossier";

const JUMP_TARGETS = [
  { label: "CEO Home",          path: "/dashboard",                section: "Home" },
  { label: "Agent Inventory",   path: "/govern/agents",            section: "Govern" },
  { label: "Policy Catalogue",  path: "/govern/policies",          section: "Govern" },
  { label: "Oversight Console", path: "/govern/oversight",         section: "Govern" },
  { label: "Incident Register", path: "/govern/incidents",         section: "Govern" },
  { label: "Compliance Centre", path: "/compliance",               section: "Compliance" },
  { label: "Cost Optimisation", path: "/measure/cost",             section: "Measure" },
  { label: "Benchmarking",      path: "/measure/benchmark",        section: "Measure" },
  { label: "Drift & Performance", path: "/measure/performance",    section: "Measure" },
  { label: "Vendor Performance", path: "/vendors/performance",     section: "Vendors" },
  { label: "Integration Hub",   path: "/vendors/integrations",     section: "Vendors" },
  { label: "Report Packs",      path: "/reports/packs",            section: "Reports" },
  { label: "Report Library",    path: "/reports/library",          section: "Reports" },
  { label: "Audit Log",         path: "/settings/audit",           section: "Settings" },
  { label: "Users & Roles",     path: "/settings/users",           section: "Settings" },
];

export default function DemoControlPanel({ isDemo, user, onResetSeed }) {
  const navigate = useNavigate();
  const { replayDemo, tenant, policies, auditEntries, requests, opportunities } = useTenantData();
  const [replayState, setReplayState] = useState(null); // { idx, total, label } | null
  const [open, setOpen] = useState(false);

  // Only CAIO in demo mode gets this shortcut at all.
  const enabled = isDemo && user?.roleCode === "CAIO";

  // Cmd/Ctrl+Shift+D toggles the panel.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);

  const jump = useCallback((path) => {
    navigate(path);
    setOpen(false);
  }, [navigate]);

  const fireDrift = useCallback(() => {
    toast.warning("Model drift alert raised", {
      description: "AGT-BANK-007 · hallucination rate ↑ 0.12% (sample 4,210 calls)",
      duration: 5000,
    });
    setOpen(false);
  }, []);

  const fireIncident = useCallback(() => {
    toast.error("Policy violation incident raised", {
      description: "INC-BANK-9999 · Unauthorised data export blocked by POL-BANK-101 (AU Data Residency).",
      duration: 5000,
    });
    setOpen(false);
  }, []);

  const resetSeed = useCallback(async () => {
    try {
      if (onResetSeed) await onResetSeed();
      toast.success("Demo seed reset", { description: "Bundle reloaded from JSON." });
    } catch (err) {
      toast.error("Reset failed", { description: err.message || "See console." });
    }
    setOpen(false);
  }, [onResetSeed]);

  const playFullDemo = useCallback(async () => {
    if (!replayDemo || replayState) return;
    setOpen(false);
    const total = 9;
    setReplayState({ idx: 0, total, label: "Starting…" });
    const startedAt = Date.now();
    try {
      const { agent, drId, opportunityId } = await replayDemo({
        stepDelayMs: 600,
        onStep: (idx, label) => setReplayState({ idx, total, label }),
      });
      if (!agent) {
        throw new Error("No agent was spawned — opportunity may already be deployed.");
      }
      setReplayState({ idx: total, total, label: "Generating dossier…" });
      // Pull the freshly-loaded bundle slices via context.
      try {
        await generateAgentDossier({
          agent,
          tenant,
          policies,
          auditEntries,
          opportunity: opportunities.find((o) => o.id === opportunityId),
          request: requests.find((r) => r.id === drId),
        });
      } catch (e) {
        // PDF is a nice-to-have at the tail end; don't fail the whole demo.
        toast.warning("Dossier skipped", { description: e?.message || "Could not generate PDF" });
      }
      const seconds = ((Date.now() - startedAt) / 1000).toFixed(1);
      toast.success(`Demo complete in ${seconds}s`, {
        description: `Agent ${agent.id} (${agent.name}) is LIVE · 7 stages cleared · dossier downloaded.`,
        action: {
          label: "Open agent",
          onClick: () => navigate(`/govern/agents/${agent.id}`),
        },
        duration: 8000,
      });
      // Navigate to agent detail so the audience lands on the live agent.
      navigate(`/govern/agents/${agent.id}`);
    } catch (err) {
      toast.error("Replay failed", { description: err?.message || "See console." });
    } finally {
      setReplayState(null);
    }
  }, [replayDemo, replayState, tenant, policies, auditEntries, opportunities, requests, navigate]);

  if (!enabled) return null;
  if (!open && !replayState) return null;

  // Replay-in-progress overlay (renders on top of everything, blocks clicks).
  if (replayState) {
    const pct = Math.round((replayState.idx / replayState.total) * 100);
    return (
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center px-4"
        data-testid="demo-replay-overlay"
      >
        <div className="absolute inset-0 bg-ink-primary/55 backdrop-blur-[3px]" />
        <div className="relative w-full max-w-md rounded-lg border border-border-subtle bg-raised shadow-e3 p-6">
          <div className="flex items-center gap-2 mb-3">
            <PlayCircle size={16} className="text-tralenex-600" />
            <p className="eyebrow text-tralenex-700">ALGOS in 10 seconds</p>
          </div>
          <p className="text-lg font-semibold text-ink-primary leading-tight" data-testid="demo-replay-step">
            {replayState.label}
          </p>
          <p className="text-meta text-ink-secondary mt-1">
            Step {replayState.idx + 1} of {replayState.total + 1}
          </p>
          <div className="mt-4 h-1.5 rounded-full bg-inset overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-tralenex-500 to-tralenex-700 transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
              data-testid="demo-replay-progress"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 anim-fade-up"
      onClick={() => setOpen(false)}
      data-testid="demo-control-overlay"
    >
      <div className="absolute inset-0 bg-ink-primary/30 backdrop-blur-[2px]" />
      <div
        className="relative w-full max-w-2xl rounded-lg border border-border-subtle bg-raised shadow-e3 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-control-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-tralenex-50">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-tralenex-700" />
            <p className="eyebrow text-tralenex-700">Demo control panel</p>
            <span className="text-meta text-ink-secondary">CAIO · demo mode only</span>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-tralenex-100"
            data-testid="demo-control-close"
            aria-label="Close demo control panel"
          >
            <X size={14} className="text-ink-secondary" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-border-subtle">
          {/* LEFT: Jump targets */}
          <div className="p-3 max-h-[420px] overflow-y-auto">
            <p className="eyebrow mb-2">Jump to page</p>
            <ul className="space-y-0.5">
              {JUMP_TARGETS.map((t) => (
                <li key={t.path}>
                  <button
                    type="button"
                    onClick={() => jump(t.path)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-inset text-left transition-colors"
                    data-testid={`demo-jump-${t.path.replace(/\//g, "-")}`}
                  >
                    <span className="text-meta text-ink-disabled w-[80px] shrink-0">{t.section}</span>
                    <span className="text-sm text-ink-primary flex-1 truncate">{t.label}</span>
                    <ArrowRight size={12} className="text-ink-disabled" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT: Actions */}
          <div className="p-3 space-y-2">
            <p className="eyebrow mb-2">Showcase</p>
            <button
              type="button"
              onClick={playFullDemo}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-md bg-gradient-to-br from-tralenex-600 to-tralenex-700 text-white shadow-e2 hover:shadow-e3 hover:from-tralenex-500 hover:to-tralenex-600 transition-all text-left"
              data-testid="demo-replay-full"
            >
              <PlayCircle size={18} className="shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold leading-tight">Replay last demo</p>
                <p className="text-meta text-white/80 leading-tight mt-0.5">
                  Opportunity → 6 approvals → agent live → dossier in ~8 s
                </p>
              </div>
              <ArrowRight size={14} className="shrink-0" />
            </button>

            <p className="eyebrow mt-4 mb-2">Trigger an event</p>
            <ActionButton
              icon={Activity}
              label="Raise Model Drift Alert"
              hint="AGT-BANK-007 · hallucination rate ↑ 0.12%"
              onClick={fireDrift}
              testId="demo-fire-drift"
            />
            <ActionButton
              icon={Bell}
              label="Raise Policy Violation Incident"
              hint="Unauthorised data export blocked · POL-BANK-101"
              onClick={fireIncident}
              testId="demo-fire-incident"
            />

            <p className="eyebrow mt-4 mb-2">Demo hygiene</p>
            <ActionButton
              icon={RotateCcw}
              label="Reset demo seed"
              hint="Reloads bundle JSON"
              onClick={resetSeed}
              testId="demo-reset-seed"
            />
            <ActionButton
              icon={UserCog}
              label="Swap persona"
              hint="Top-right · DEMO pill"
              onClick={() => { setOpen(false); toast.info("Use the gold DEMO pill in the header to swap."); }}
              testId="demo-swap-persona"
            />

            <div className="mt-4 px-2 py-2 rounded bg-inset/60 text-[11px] text-ink-secondary flex items-start gap-2">
              <Keyboard size={12} className="mt-0.5 shrink-0" />
              <div className="leading-snug">
                <p><kbd className="font-mono text-ink-primary">⌘/Ctrl ⇧ D</kbd> toggle this panel</p>
                <p><kbd className="font-mono text-ink-primary">⌘/Ctrl K</kbd> focus global search</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, hint, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-2.5 py-2 rounded border border-border-subtle hover:border-tralenex-300 hover:bg-tralenex-50/50 text-left transition-colors"
      data-testid={testId}
    >
      <Icon size={14} className="text-tralenex-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink-primary">{label}</p>
        <p className="text-meta text-ink-secondary truncate">{hint}</p>
      </div>
      <ArrowRight size={12} className="text-ink-disabled" />
    </button>
  );
}
